import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import type { ReactNode } from 'react';
import ExchangeCatalogSelector from './ExchangeCatalogSelector';
import type useCustomizationPolicy from './useCustomizationPolicy';
import type {
  CustomizationConfiguration,
  CustomizationProhibitedProperty,
} from './CustomizationPolicy.contract';

function emptyConfiguration(): CustomizationConfiguration {
  return {
    optionName: '',
    standardOptionValue: '',
    customizedOptionValue: '',
    heightPropertyKey: '',
    heightLabel: '',
    heightValues: [],
    originalClassification: {
      customizedSkuSuffix: '',
      prohibitedProperties: [],
      prohibitedProductIds: [],
      ignoredPropertyKeys: [],
    },
    originalRestocking: {
      currency: 'USD',
      amountPerEligibleUnit: '25.00',
      standardExempt: true,
    },
    replacementReturnPolicy: 'NO_REPEAT_RETURN_OR_EXCHANGE',
    fulfillmentMode: 'MANUAL_TEAM_ACK',
  };
}
export default function CustomizedGarmentsSettings({
  storeId,
  policy,
  disabled: parentDisabled = false,
  instructionsSetup,
}: {
  storeId: string;
  policy: ReturnType<typeof useCustomizationPolicy>;
  disabled?: boolean;
  instructionsSetup?: ReactNode;
}) {
  const draft = policy.draft,
    configuration = draft?.configuration;
  const disabled =
    parentDisabled ||
    policy.saving ||
    policy.loading ||
    policy.unavailable ||
    !!policy.readError;
  const change = (next: CustomizationConfiguration) => {
    if (draft && !disabled) policy.change({ ...draft, configuration: next });
  };
  const edit = (patch: Partial<CustomizationConfiguration>) => {
    if (configuration) change({ ...configuration, ...patch });
  };
  const classification = configuration?.originalClassification;
  const editClassification = (
    patch: Partial<CustomizationConfiguration['originalClassification']>,
  ) => {
    if (configuration)
      edit({
        originalClassification: {
          ...configuration.originalClassification,
          ...patch,
        },
      });
  };
  const input = (
    label: string,
    key:
      | 'optionName'
      | 'standardOptionValue'
      | 'customizedOptionValue'
      | 'heightPropertyKey'
      | 'heightLabel',
    maximum = 120,
  ) => (
    <label className="block text-xs font-semibold text-gray-600">
      {label}
      <input
        aria-label={label}
        value={configuration?.[key] || ''}
        maxLength={maximum}
        onChange={(event) => edit({ [key]: event.target.value })}
        className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900"
      />
    </label>
  );
  function addHeight() {
    if (!configuration || disabled || configuration.heightValues.length >= 32)
      return;
    const existingIds = new Set(
      configuration.heightValues.map((height) => height.id),
    );
    let next = 1;
    while (existingIds.has(`height-${next}`)) next++;
    edit({
      heightValues: [
        ...configuration.heightValues,
        { id: `height-${next}`, label: '', value: '' },
      ],
    });
  }
  const editProperty = (
    index: number,
    property: CustomizationProhibitedProperty,
  ) => {
    if (classification)
      editClassification({
        prohibitedProperties: classification.prohibitedProperties.map(
          (entry, candidate) => (candidate === index ? property : entry),
        ),
      });
  };
  return (
    <section
      aria-label="Customized garments"
      className="space-y-4 rounded-xl border border-gray-200 bg-white p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Customized garments
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Offer a fixed list of replacement heights and keep production
            instructions with the original order.
          </p>
        </div>
        {draft && configuration && (
          <Toggle
            ariaLabel="Enable customized garment exchanges"
            leftLabel="Enable customized garment exchanges"
            isEnabled={draft.enabled}
            disabled={disabled}
            onChange={(enabled) => policy.change({ ...draft, enabled })}
          />
        )}
      </div>
      {!draft ? (
        <div
          role={policy.loading ? 'status' : 'alert'}
          className="text-sm text-gray-600"
        >
          {policy.loading
            ? 'Loading customized garment settings…'
            : 'Customized garment settings are unavailable.'}
        </div>
      ) : !configuration ? (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm text-gray-600">
            Customized garment exchanges are off. Set up your store’s exact
            option names, height choices and original-item exclusions to use
            this feature.
          </p>
          <Button
            variant="outlined"
            disabled={disabled}
            onClick={() =>
              policy.change({
                enabled: false,
                configuration: emptyConfiguration(),
              })
            }
          >
            Set up customized garments
          </Button>
        </div>
      ) : (
        <fieldset disabled={disabled} className="space-y-4">
          {!draft.enabled && (
            <p className="rounded-lg bg-gray-50 p-3 text-xs leading-5 text-gray-600">
              Turning this off stops new customized replacements and does not
              waive fees on customized originals. Your configuration is kept.
            </p>
          )}
          {instructionsSetup}
          <div className="grid gap-3 md:grid-cols-2">
            {input('Shopify customization option', 'optionName')}
            {input('Customer height label', 'heightLabel')}
            {input('Standard option value', 'standardOptionValue')}
            {input('Customized option value', 'customizedOptionValue')}
            {input('Shopify height field', 'heightPropertyKey', 64)}
            <label className="block text-xs font-semibold text-gray-600">
              Customized SKU suffix
              <input
                aria-label="Customized SKU suffix"
                maxLength={32}
                value={classification!.customizedSkuSuffix}
                onChange={(event) =>
                  editClassification({
                    customizedSkuSuffix: event.target.value,
                  })
                }
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-900"
              />
            </label>
          </div>
          <p className="text-xs leading-5 text-gray-500">
            Copy exact names and values from Shopify. A customized original
            needs matching option, SKU and order-item height evidence. Unclear
            or additional customization stays in review.
          </p>
          <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Height choices
            </h3>
            <p className="text-xs leading-5 text-gray-500">
              Use the exact saved Shopify value for each height. The customer
              label may be easier to read. Existing values, including spaces,
              are preserved.
            </p>
            <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
              {configuration.heightValues.map((height, index) => (
                <div
                  key={height.id}
                  className="grid gap-2 rounded-lg bg-gray-50 p-3 sm:grid-cols-[1fr_1fr_auto]"
                >
                  <label className="text-xs font-medium text-gray-600">
                    Customer label
                    <input
                      aria-label={`Height ${index + 1} customer label`}
                      maxLength={120}
                      value={height.label}
                      onChange={(event) =>
                        edit({
                          heightValues: configuration.heightValues.map(
                            (entry, candidate) =>
                              candidate === index
                                ? { ...entry, label: event.target.value }
                                : entry,
                          ),
                        })
                      }
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm text-gray-900"
                    />
                  </label>
                  <label className="text-xs font-medium text-gray-600">
                    Shopify value
                    <input
                      aria-label={`Height ${index + 1} Shopify value`}
                      maxLength={120}
                      value={height.value}
                      onChange={(event) =>
                        edit({
                          heightValues: configuration.heightValues.map(
                            (entry, candidate) =>
                              candidate === index
                                ? { ...entry, value: event.target.value }
                                : entry,
                          ),
                        })
                      }
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm text-gray-900"
                    />
                  </label>
                  <button
                    type="button"
                    aria-label={`Remove height ${index + 1}`}
                    onClick={() =>
                      edit({
                        heightValues: configuration.heightValues.filter(
                          (_, candidate) => candidate !== index,
                        ),
                      })
                    }
                    className="self-end rounded px-2 py-2 text-sm text-gray-500 hover:bg-gray-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <Button
              variant="outlined"
              disabled={disabled || configuration.heightValues.length >= 32}
              onClick={addHeight}
            >
              Add height choice
            </Button>
            <p className="text-xs text-gray-500">
              {configuration.heightValues.length} of 32 choices. One selected
              height applies to every unit in the replacement selection.
            </p>
          </div>
          <details className="rounded-lg border border-gray-200 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900">
              Original-item exclusions and other order fields
            </summary>
            <div className="mt-3 space-y-4">
              <p className="text-xs leading-5 text-gray-500">
                Exclude body measurements or other customizations that your
                policy does not accept. These fields classify the purchased
                item; they do not ask customers for extra measurements.
              </p>
              {classification!.prohibitedProperties.map((property, index) => (
                <div
                  key={index}
                  className="grid gap-3 rounded-lg bg-gray-50 p-3 sm:grid-cols-2"
                >
                  <label className="text-xs font-medium text-gray-600">
                    Order-item field
                    <input
                      aria-label={`Excluded customization ${index + 1} field`}
                      maxLength={64}
                      value={property.key}
                      onChange={(event) =>
                        editProperty(index, {
                          ...property,
                          key: event.target.value,
                        })
                      }
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                    />
                  </label>
                  <label className="text-xs font-medium text-gray-600">
                    Exclude when
                    <select
                      aria-label={`Excluded customization ${
                        index + 1
                      } condition`}
                      value={property.match}
                      onChange={(event) =>
                        editProperty(
                          index,
                          event.target.value === 'ANY_NONEMPTY'
                            ? { key: property.key, match: 'ANY_NONEMPTY' }
                            : {
                                key: property.key,
                                match: 'ONE_OF',
                                values: [],
                              },
                        )
                      }
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                    >
                      <option value="ANY_NONEMPTY">Any value is present</option>
                      <option value="ONE_OF">
                        The value matches this list
                      </option>
                    </select>
                  </label>
                  {property.match === 'ONE_OF' && (
                    <label className="text-xs font-medium text-gray-600 sm:col-span-2">
                      Excluded values, one per line
                      <textarea
                        aria-label={`Excluded customization ${
                          index + 1
                        } values`}
                        rows={3}
                        value={property.values.join('\n')}
                        onChange={(event) =>
                          editProperty(index, {
                            ...property,
                            values:
                              event.target.value === ''
                                ? []
                                : event.target.value.split('\n'),
                          })
                        }
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      editClassification({
                        prohibitedProperties:
                          classification!.prohibitedProperties.filter(
                            (_, candidate) => candidate !== index,
                          ),
                      })
                    }
                    className="text-left text-xs font-medium text-gray-600"
                  >
                    Remove exclusion
                  </button>
                </div>
              ))}
              <Button
                variant="outlined"
                disabled={
                  disabled || classification!.prohibitedProperties.length >= 32
                }
                onClick={() =>
                  editClassification({
                    prohibitedProperties: [
                      ...classification!.prohibitedProperties,
                      { key: '', match: 'ANY_NONEMPTY' },
                    ],
                  })
                }
              >
                Add customization exclusion
              </Button>
              <ExchangeCatalogSelector
                storeId={storeId}
                disabled={disabled}
                searchLabel="Search products excluded for customization"
                productLabel="Products excluded for customization"
                selected={{
                  productIds: classification!.prohibitedProductIds,
                  collectionIds: [],
                }}
                onChange={(selection) =>
                  editClassification({
                    prohibitedProductIds: selection.productIds,
                  })
                }
              />
              <label className="block text-xs font-medium text-gray-600">
                Other order-item fields to ignore, one per line
                <textarea
                  aria-label="Other order-item fields to ignore"
                  rows={3}
                  value={classification!.ignoredPropertyKeys.join('\n')}
                  onChange={(event) =>
                    editClassification({
                      ignoredPropertyKeys:
                        event.target.value === ''
                          ? []
                          : event.target.value.split('\n'),
                    })
                  }
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                />
              </label>
              <p className="text-xs leading-5 text-gray-500">
                List only fields your team verified do not describe garment
                customization. Unknown fields stay in review.
              </p>
            </div>
          </details>
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-5 text-gray-600">
            <p className="font-semibold text-gray-900">
              25.00 USD per eligible customized garment
            </p>
            <p>
              Standard-height originals are exempt. The fee depends on the
              original item and is separate from label costs and replacement
              price differences.
            </p>
            <p>
              The 25.00 USD restocking charge still applies when Delivery
              Guarantee covers return shipping.
            </p>
            <p>
              Replacement garments cannot be returned or exchanged again. Other
              untouched original items keep their normal eligibility.
            </p>
            <p>
              Before processing a replacement, the team must acknowledge its
              exact height and quantity. Verified instructions stay on the
              original Shopify order.
            </p>
          </div>
        </fieldset>
      )}
      {policy.error && (
        <p role="alert" className="text-sm text-red-700">
          {policy.error}
        </p>
      )}
      {policy.saved && (
        <p role="status" className="text-sm font-medium text-green-700">
          Customized garment settings saved.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {policy.dirty && (
          <span className="text-xs font-medium text-amber-700">
            Unsaved customized garment settings
          </span>
        )}
        <Button
          disabled={
            disabled ||
            !policy.dirty ||
            policy.needsReload ||
            (policy.source?.revision || 0) >= 2147483647
          }
          isLoading={policy.saving}
          onClick={() => policy.save()}
        >
          Save customized garment settings
        </Button>
        {(policy.error || policy.unavailable || policy.readError) && (
          <Button
            variant="outlined"
            disabled={policy.saving || policy.loading}
            onClick={() => policy.reload()}
          >
            Reload customized garment settings
          </Button>
        )}
      </div>
    </section>
  );
}
